import { EDD_METHOD_OPTIONS } from "features/pregnancy/constants/eddMethodOptions";

/**
 * Why this exists: `pregnancy.edd_method` is an unconstrained free-text column that two
 * different vocabularies have been written into. The dropdown has always posted short
 * internal codes ("LMP", "USS", "IVF", "Other"), while the HSE MNCMS spec requires the EDD
 * Method OBX to carry the wording "Last Menstrual Period" / "Ultrasound" / "Advanced
 * Reproductive Technology" / "Unknown" — and the backend emits the stored string into the
 * HL7 message verbatim, so records exist (e.g. UAT pregnancy 86) holding the spec wording
 * instead of a code. A stored value therefore has to be resolved back to an option value
 * before a <Select> can preselect it; without that the Select falls through to its
 * placeholder and the form behaves as though no method had ever been chosen.
 */
const SYNONYMS_BY_OPTION_VALUE = {
  LMP: ["lmp", "last menstrual period"],
  USS: ["uss", "ultrasound", "scan"],
  IVF: [
    "ivf",
    "art",
    "advanced reproductive technology",
    "assisted reproductive technology",
  ],
  Other: ["other", "unknown"],
};

const buildOptionValueBySynonym = () => {
  const lookup = new Map();

  EDD_METHOD_OPTIONS.forEach(({ label, value }) => {
    lookup.set(value.toLowerCase(), value);
    lookup.set(label.toLowerCase(), value);
    (SYNONYMS_BY_OPTION_VALUE[value] ?? []).forEach((synonym) =>
      lookup.set(synonym, value)
    );
  });

  return lookup;
};

const OPTION_VALUE_BY_SYNONYM = buildOptionValueBySynonym();

const toLookupKey = (storedValue) =>
  typeof storedValue === "string" ? storedValue.trim().toLowerCase() : "";

/**
 * Resolves a stored EDD method to one of the dropdown option values, or "" when nothing
 * matches (the Select then shows its placeholder and validation forces an explicit choice).
 */
export const normaliseEddMethod = (storedValue) =>
  OPTION_VALUE_BY_SYNONYM.get(toLookupKey(storedValue)) ?? "";

/**
 * Human-readable EDD method for read-only views. Falls back to the stored string so an
 * unrecognised value is still shown rather than silently hidden.
 */
export const getEddMethodLabel = (storedValue) => {
  const optionValue = normaliseEddMethod(storedValue);
  const option = EDD_METHOD_OPTIONS.find(({ value }) => value === optionValue);

  return option?.label ?? storedValue ?? "";
};
